import React from 'react';
import { Badge, Button } from '@/components/ui';
import type { AgentPromptVersion } from '@/services/admin/AdminService';

interface VersionHistoryProps {
  versions: AgentPromptVersion[];
  currentVersion: string;
  onRollback: (version: string) => void;
  onViewDiff: (version: AgentPromptVersion) => void;
  loading?: boolean;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({
  versions,
  currentVersion,
  onRollback,
  onViewDiff,
  loading = false,
}) => {
  // Sort versions by creation date (newest first)
  const sortedVersions = [...versions].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  if (sortedVersions.length === 0) {
    return (
      <div className="text-sm text-secondary-500 py-4">
        No version history available.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-secondary-700 mb-3">
        Version History
      </h4>
      <div className="divide-y divide-secondary-100 border border-secondary-200 rounded-lg">
        {sortedVersions.map((version) => {
          const isActive = version.version === currentVersion;
          return (
            <div
              key={version.version}
              className={`p-3 ${isActive ? 'bg-primary-50' : 'hover:bg-secondary-50'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-medium text-secondary-900">
                    v{version.version}
                  </span>
                  {isActive && (
                    <Badge variant="success" className="text-xs">
                      Active
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewDiff(version)}
                  >
                    View
                  </Button>
                  {!isActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRollback(version.version)}
                      loading={loading}
                    >
                      Rollback
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm text-secondary-600 mt-1">
                {version.description}
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs text-secondary-500">
                <span>
                  {new Date(version.created_at).toLocaleDateString()} at{' '}
                  {new Date(version.created_at).toLocaleTimeString()}
                </span>
                <span>by {version.created_by}</span>
              </div>
              {version.performance_notes && (
                <p className="text-xs text-secondary-500 mt-1 italic">
                  Notes: {version.performance_notes}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VersionHistory;
